/**
 * StoryStickerPicker — emoji-based sticker picker for Stories (Phase 2).
 *
 * Slides up from the bottom over the StoryEditor. Tapping any emoji adds it
 * as a 'sticker' overlay centered on the canvas. The emoji palette is
 * curated for stories (reactions, hearts, faces, animals, food, party,
 * weather…) and stays small enough to render quickly on low-end Android.
 */
import React, { useState } from 'react';
import { X } from 'lucide-react';

const STICKER_CATEGORIES = {
  '⭐ Tendances': [
    '❤️','🔥','😍','😂','✨','💯','👏','🙌','🤩','💖','💕','🥰','😎','🤯','🎉','🎊','💎','🌟','⚡','🌈',
  ],
  '😀 Visages': [
    '😀','😃','😄','😁','😆','😅','🤣','😂','🙂','🙃','😉','😊','😇','🥰','😍','🤩','😘','😗','😚','😙',
    '😋','😛','😜','🤪','😝','🤑','🤗','🤔','🤐','🤨','😐','😑','😶','😏','😒','🙄','😬','😮','😯','😳',
    '🥺','😨','😰','😥','😢','😭','😱','😖','😣','😞','😓','😩','😫','🥱','😤','😡','😠','🤬','😈','👿',
  ],
  '👋 Mains & Gestes': [
    '👋','🤚','🖐','✋','🖖','👌','🤌','🤏','✌️','🤞','🤟','🤘','🤙','👈','👉','👆','🖕','👇','☝️','👍',
    '👎','✊','👊','🤛','🤜','👏','🙌','👐','🤲','🤝','🙏','✍️','💅','🤳','💪','🦾','🦿','🦵','🦶','👂',
  ],
  '🐶 Animaux & Nature': [
    '🐶','🐱','🐭','🐹','🐰','🦊','🐻','🐼','🐻‍❄️','🐨','🐯','🦁','🐮','🐷','🐸','🐵','🙈','🙉','🙊','🐒',
    '🦄','🐝','🐛','🦋','🐌','🐞','🐢','🐍','🦖','🐳','🐬','🐠','🐟','🦈','🐙','🦀','🦐','🌸','🌺','🌻',
    '🌹','🌷','🌼','🌳','🌲','🌴','🌵','🍀','🍁','🍂','🍃','🌍','🌎','🌏','🌙','☀️','⭐','🌟','✨','⚡',
  ],
  '🍕 Nourriture & Boissons': [
    '🍕','🍔','🍟','🌭','🥪','🌮','🌯','🥙','🥗','🍝','🍜','🍲','🍛','🍣','🍱','🥟','🍤','🍙','🍚','🍘',
    '🍩','🍪','🎂','🍰','🧁','🥧','🍫','🍬','🍭','🍮','🍯','🍼','🥛','☕','🍵','🍶','🍺','🍷','🍸','🍹',
    '🍎','🍏','🍊','🍋','🍌','🍉','🍇','🍓','🫐','🍈','🍒','🍑','🥭','🍍','🥥','🥝','🍅','🍆','🥑','🌶',
  ],
  '🎉 Fête & Objets': [
    '🎉','🎊','🎈','🎁','🎀','🪅','🎂','🍰','🥂','🍾','🪩','💐','💝','💍','💎','👑','🎩','🎓','🧢','👒',
    '👓','🕶','💋','💄','💅','📸','📷','🎥','🎬','🎤','🎧','🎵','🎶','🎼','🎹','🎸','🥁','🎺','🎷','🪕',
    '⚽','🏀','🏈','⚾','🎾','🏐','🏉','🎱','🏓','🏸','🏒','🏑','🥊','⛳','🏆','🥇','🥈','🥉','🎯','🏁',
  ],
  '❤️ Cœurs & Symboles': [
    '❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖','💘','💝','💟','♾',
    '☮️','✝️','☪️','🕉','☸️','✡️','🔯','🕎','☯️','☦️','🛐','⛎','♈','♉','♊','♋','♌','♍','♎','♏',
    '🆗','🆕','🆒','🆙','🆓','🅾️','♻️','✅','❌','⭕','🔴','🟠','🟡','🟢','🔵','🟣','⚫','⚪','🟤','🚫',
  ],
};

const StoryStickerPicker = ({ onPick, onClose }) => {
  const tabs = Object.keys(STICKER_CATEGORIES);
  const [activeTab, setActiveTab] = useState(tabs[0]);

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[120] bg-gradient-to-t from-black via-black/95 to-black/80 backdrop-blur-md border-t border-white/10 rounded-t-3xl shadow-2xl"
      data-testid="story-sticker-picker"
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <p className="text-white font-semibold text-sm">Stickers & emojis</p>
        <button
          onClick={onClose}
          className="p-1.5 rounded-full bg-white/10 hover:bg-white/20"
          data-testid="story-sticker-close"
          aria-label="Fermer"
        >
          <X className="w-4 h-4 text-white" />
        </button>
      </div>

      {/* Category tabs */}
      <div className="flex gap-1.5 px-3 pb-2 overflow-x-auto no-scrollbar">
        {tabs.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              activeTab === tab
                ? 'bg-white text-black'
                : 'bg-white/10 text-white/80 hover:bg-white/20'
            }`}
            data-testid={`sticker-tab-${tab}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Emoji grid */}
      <div className="grid grid-cols-8 sm:grid-cols-10 gap-1 px-3 pb-3 max-h-[40vh] overflow-y-auto">
        {STICKER_CATEGORIES[activeTab].map((e, i) => (
          <button
            key={`${activeTab}-${i}`}
            onClick={() => onPick(e)}
            className="aspect-square flex items-center justify-center text-2xl rounded-lg hover:bg-white/10 active:scale-90 transition-transform"
            data-testid={`sticker-emoji-${e}`}
          >
            {e}
          </button>
        ))}
      </div>
    </div>
  );
};

export default StoryStickerPicker;
